import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { uploadFile, fileUrl } from "@/api";
import { toast } from "sonner";
import { FileText, Loader2, Upload, Trash2, RotateCw } from "lucide-react";

const ACCEPTED = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];
const MAX_BYTES = 10 * 1024 * 1024; // 10MB

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function UploadFullMenu({ menu, onUploaded, onRemoved }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const hasMenu = !!menu?.full_menu_url;
  const isPdf = menu?.full_menu_content_type === "application/pdf";

  const pick = () => inputRef.current?.click();

  const handleFiles = async (file) => {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      toast.error("Only PDF, JPG, PNG or WEBP files are allowed");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("File too large (max 10MB)");
      return;
    }
    setBusy(true);
    try {
      const res = await uploadFile(file);
      await onUploaded?.({
        file_id: res.id,
        url: res.url,
        content_type: file.type,
        filename: file.name,
      });
      toast.success("Menu uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const handleInput = async (e) => {
    const file = e.target.files?.[0];
    await handleFiles(file);
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    await handleFiles(file);
  };

  const remove = async () => {
    if (!window.confirm("Remove the full menu?")) return;
    setBusy(true);
    try {
      await onRemoved?.();
      toast.success("Menu removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-5"
      data-testid="full-menu-upload-section"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base font-semibold">Upload full menu</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Upload a PDF or image of your complete menu. Customers will see it on your public page.
          </p>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf,image/jpeg,image/jpg,image/png,image/webp"
        className="hidden"
        onChange={handleInput}
        data-testid="full-menu-input"
      />

      {hasMenu ? (
        <div
          className="flex items-center gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4"
          data-testid="full-menu-preview"
        >
          {isPdf ? (
            <div
              className="h-20 w-20 rounded-md bg-white border border-slate-200 flex items-center justify-center text-[#E8650A] flex-shrink-0"
              data-testid="full-menu-pdf-icon"
            >
              <FileText size={36} />
            </div>
          ) : (
            <img
              src={fileUrl(menu.full_menu_url)}
              alt="Menu preview"
              className="h-20 w-20 rounded-md object-cover border border-slate-200 flex-shrink-0 bg-white"
              data-testid="full-menu-image-thumb"
            />
          )}
          <div className="min-w-0 flex-1">
            <div
              className="font-medium text-sm text-slate-800 truncate"
              data-testid="full-menu-filename"
            >
              {menu.full_menu_filename || (isPdf ? "Menu.pdf" : "Menu image")}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {isPdf ? "PDF" : (menu.full_menu_content_type || "Image").replace("image/", "").toUpperCase()}
              {menu.full_menu_uploaded_at && (
                <> · Uploaded {formatDate(menu.full_menu_uploaded_at)}</>
              )}
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={pick}
                disabled={busy}
                data-testid="full-menu-replace"
              >
                {busy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                ) : (
                  <RotateCw className="h-3.5 w-3.5 mr-1.5" />
                )}
                Replace
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={remove}
                disabled={busy}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="full-menu-remove"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Remove
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={pick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              pick();
            }
          }}
          onDragEnter={handleDragOver}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={
            "rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors " +
            (dragOver
              ? "border-[#E8650A] bg-[#FDECE2]"
              : "border-slate-300 hover:border-[#E8650A] hover:bg-slate-50")
          }
          data-testid="full-menu-dropzone"
          data-dragover={dragOver ? "true" : "false"}
        >
          <div className="flex flex-col items-center gap-2">
            <div className="h-12 w-12 rounded-full bg-[#FDECE2] text-[#E8650A] grid place-items-center">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
            </div>
            <div className="text-sm font-medium text-slate-800">
              {busy ? "Uploading…" : dragOver ? "Drop the file here" : "Drag & drop your menu, or click to browse"}
            </div>
            <div className="text-xs text-slate-500">
              PDF, JPG, PNG or WEBP · up to 10MB
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
